import React, { useLayoutEffect, useRef, useState } from 'react'
import { Marker } from 'react-simple-maps'

import { type MapLabelCandidate, type MapLabelPlacement, layoutMapLabels } from './mapLabelLayout'
import { type CountryLabelPresentation, pointCountryIds } from './mapPresentation'
import { worldProjection } from './mapProjection'
import styles from './styles.module.scss'

export type MapLabel = {
  id: string
  coordinates: [number, number]
  presentation: CountryLabelPresentation
  light: boolean
  priority: number
}

const MapLabels = ({
  labels,
  zoom,
  countryPaths,
  frameRef,
}: {
  labels: MapLabel[]
  zoom: number
  countryPaths: React.MutableRefObject<Record<string, SVGPathElement | null>>
  frameRef: React.RefObject<HTMLDivElement>
}) => {
  const nodes = useRef<Record<string, SVGTextElement | null>>({})
  const [placements, setPlacements] = useState<Record<string, MapLabelPlacement>>({})
  const [occludedIds, setOccludedIds] = useState<Set<string>>(new Set())

  useLayoutEffect(() => {
    let active = true
    const measure = () => {
      if (!active) return

      const candidates = labels.flatMap((label): MapLabelCandidate[] => {
        const node = nodes.current[label.id]
        const path = countryPaths.current[label.id]
        const point = worldProjection(label.coordinates)
        if (!node || !point || (!pointCountryIds.has(label.id) && !path)) return []

        const bounds = node.getBBox()
        if (!bounds.width || !bounds.height) return []

        return [
          {
            id: label.id,
            point,
            bounds,
            baseFontSize: label.presentation.size === 'small' ? 12 : 14,
            priority: label.priority,
            allowOffset: pointCountryIds.has(label.id),
            containsPoint:
              path && !pointCountryIds.has(label.id)
                ? ([x, y]) => path.isPointInFill(new DOMPoint(x, y))
                : undefined,
          },
        ]
      })
      const next = layoutMapLabels(candidates, zoom)
      setPlacements((current) =>
        Object.keys(current).length === Object.keys(next).length &&
        Object.entries(next).every(
          ([id, placement]) =>
            current[id]?.fontSize === placement.fontSize &&
            current[id]?.offsetX === placement.offsetX &&
            current[id]?.offsetY === placement.offsetY
        )
          ? current
          : next
      )
    }

    measure()
    void document.fonts.ready.then(measure)
    return () => {
      active = false
    }
  }, [countryPaths, labels, zoom])

  useLayoutEffect(() => {
    const frame = frameRef.current
    if (!frame) return

    const syncOcclusion = () => {
      const frameBounds = frame.getBoundingClientRect()
      const blockers = [
        { element: frame.querySelector('[aria-label="Map zoom controls"]'), margin: 4 },
        { element: frame.querySelector('[data-testid="country-details"]'), margin: 4 },
        // The callout sits right above the labels by design (Figma overlaps RUSSIA's text box by
        // 2px), so a few pixels of line-height padding under it are tolerated.
        {
          element: frame.querySelector('[data-testid="map-callout"] > [role="tooltip"]'),
          margin: -4,
        },
      ].flatMap(({ element, margin }) =>
        element ? [{ rect: element.getBoundingClientRect(), margin }] : []
      )
      const next = new Set(
        labels.flatMap(({ id }) => {
          const node = nodes.current[id]
          if (!node || !placements[id]) return []
          const bounds = node.getBoundingClientRect()
          // A label cut by the frame edge reads as a typo ("BEKISTAN"): hide it until it fits.
          const insideFrame =
            bounds.left >= frameBounds.left &&
            bounds.right <= frameBounds.right &&
            bounds.top >= frameBounds.top &&
            bounds.bottom <= frameBounds.bottom
          const touchesFrame =
            bounds.right > frameBounds.left &&
            bounds.left < frameBounds.right &&
            bounds.bottom > frameBounds.top &&
            bounds.top < frameBounds.bottom
          const clippedByFrame = touchesFrame && !insideFrame
          const covered = blockers.some(
            ({ rect, margin }) =>
              bounds.left < rect.right + margin &&
              bounds.right + margin > rect.left &&
              bounds.top < rect.bottom + margin &&
              bounds.bottom + margin > rect.top
          )
          return clippedByFrame || covered ? [id] : []
        })
      )
      setOccludedIds((current) =>
        current.size === next.size && [...next].every((id) => current.has(id)) ? current : next
      )
    }

    const frameId = window.requestAnimationFrame(syncOcclusion)
    window.addEventListener('resize', syncOcclusion)
    return () => {
      window.cancelAnimationFrame(frameId)
      window.removeEventListener('resize', syncOcclusion)
    }
  }, [frameRef, labels, placements, zoom])

  return (
    <>
      {labels.map(({ id, coordinates, presentation, light }) => {
        const baseFontSize = presentation.size === 'small' ? 12 : 14
        const placement = placements[id]
        const offsetX = placement?.offsetX ?? 0
        const offsetY = placement?.offsetY ?? 0
        const isVisible = Boolean(placement) && !occludedIds.has(id)

        return (
          <Marker coordinates={coordinates} key={id} aria-hidden>
            {/* A leader line without its label would point at nothing. */}
            {isVisible && offsetY !== 0 ? (
              <line
                className={styles.countryLabelLeader}
                y2={offsetY / zoom}
                strokeWidth={1 / zoom}
              />
            ) : null}
            {(!isVisible || offsetY !== 0) && pointCountryIds.has(id) ? (
              <g className={styles.smallCountryPoint} transform={`scale(${1 / zoom})`}>
                <circle r={3.5} />
              </g>
            ) : null}
            <text
              ref={(node) => {
                nodes.current[id] = node
              }}
              className={`${styles.countryLabel}${presentation.size === 'small' ? ` ${styles.countryLabelSmall}` : ''}${light ? ` ${styles.countryLabelLight}` : ''}`}
              data-map-label={id}
              textAnchor="middle"
              dominantBaseline="middle"
              visibility={isVisible ? 'visible' : 'hidden'}
              transform={`translate(${offsetX / zoom} ${offsetY / zoom}) scale(${(placement?.fontSize ?? baseFontSize) / baseFontSize / zoom})`}
            >
              {presentation.text}
            </text>
          </Marker>
        )
      })}
    </>
  )
}

export default MapLabels
